import { parseJwt } from '../utils';
import { ApiService } from './ApiService';

const REFRESH_THRESHOLD_SECONDS = 300; // Refresh 5 minutes before expiry
const CHECK_INTERVAL_MS = 60000; // Check every minute
const RETRY_DELAY_MS = 500; // Wait 5 ms before allowing retry after failure

let intervalTimer: number | undefined;
let isRefreshing = false;
let lastRefreshAttempt = 0;
let refreshPromise: Promise<void> | null = null;
let onTokenRefreshedCallback: ((token: string, refreshToken: string) => void) | undefined;

/**
 * Checks if the current token needs refresh based on expiry threshold.
 */
function shouldRefreshToken(): boolean {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;

    const jwt = parseJwt(token);
    const expiresAt = jwt.exp;
    const now = Date.now() / 1000;

    return expiresAt - now < REFRESH_THRESHOLD_SECONDS;
  } catch {
    return false;
  }
}

/**
 * Attempts to refresh the authentication token using the stored refresh token.
 */
async function performTokenRefresh(): Promise<void> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  const now = Date.now();
  if (now - lastRefreshAttempt < RETRY_DELAY_MS) {
    return;
  }

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    return;
  }

  isRefreshing = true;
  lastRefreshAttempt = now;

  refreshPromise = (async () => {
    try {
      const apiService = new ApiService();
      const result = await apiService.api.auth.authRefreshAuthToken({ refreshToken });

      if (result.data) {
        const newToken = result.data.token;
        const newRefreshToken = result.data.refreshToken;

        // Update tokens in localStorage
        localStorage.setItem('token', newToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        // Notify callback if registered
        if (onTokenRefreshedCallback) {
          onTokenRefreshedCallback(newToken, newRefreshToken);
        }

        // Reset the retry delay on success
        lastRefreshAttempt = 0;
      }
    } catch (error) {
      console.error('Token RefreshFailed', error);
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Background check that runs periodically.
 */
async function backgroundCheck(): Promise<void> {
  if (shouldRefreshToken()) {
    await performTokenRefresh();
  }
}

/**
 * Starts the background token refresh process.
 * Checks token expiry periodically and refreshes when needed.
 */
export function startTokenRefreshWatcher(onTokenRefreshed?: (token: string, refreshToken: string) => void): void {
  if (intervalTimer) {
    return;
  }

  onTokenRefreshedCallback = onTokenRefreshed;

  backgroundCheck();

  intervalTimer = window.setInterval(backgroundCheck, CHECK_INTERVAL_MS);
}

/**
 * Stops the background token refresh process.
 */
export function stopTokenRefreshWatcher(): void {
  if (intervalTimer) {
    clearInterval(intervalTimer);
    intervalTimer = undefined;
  }

  onTokenRefreshedCallback = undefined;
  isRefreshing = false;
  lastRefreshAttempt = 0;
  refreshPromise = null;
}
